import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'
import { BarChart3, TrendingUp, CheckCircle2, Clock, AlertTriangle } from 'lucide-react'

const STATUS_COLORS = {
  pending:     '#9ca3af',
  in_progress: '#3b82f6',
  review:      '#a855f7',
  completed:   '#22c55e',
  blocked:     '#ef4444',
}
const STATUS_LABELS = {
  pending: 'Pendiente', in_progress: 'En progreso',
  review: 'En revisión', completed: 'Completado', blocked: 'Bloqueado',
}
const CONS_COLORS = ['#22c55e', '#eab308', '#1e3a5f']

export default function AnalyticsPage() {
  const [loading, setLoading]         = useState(true)
  const [consultancies, setConsultancies] = useState([])
  const [tasks, setTasks]             = useState([])
  const [progressMap, setProgressMap] = useState({})

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    setLoading(true)
    const [consRes, tasksRes] = await Promise.all([
      supabase.from('consultancies').select('id, name, client_company, status').order('created_at', { ascending: false }),
      supabase.from('tasks').select('id, status, priority, due_date, sections(phases(consultancy_id))'),
    ])
    const cons  = consRes.data  ?? []
    const tks   = tasksRes.data ?? []

    setConsultancies(cons)
    setTasks(tks)

    // Progress per consultancy
    const pMap = {}
    tks.forEach(t => {
      const cid = t.sections?.phases?.consultancy_id
      if (!cid) return
      if (!pMap[cid]) pMap[cid] = { total: 0, done: 0 }
      pMap[cid].total++
      if (t.status === 'completed') pMap[cid].done++
    })
    setProgressMap(pMap)
    setLoading(false)
  }

  // ── Derived data ─────────────────────────────────────
  const tasksByStatus = Object.entries(STATUS_LABELS).map(([key, label]) => ({
    label,
    value: tasks.filter(t => t.status === key).length,
    color: STATUS_COLORS[key],
  })).filter(d => d.value > 0)

  const tasksByPriority = [
    { label: 'Alta',  value: tasks.filter(t => t.priority === 'high').length,   color: '#ef4444' },
    { label: 'Media', value: tasks.filter(t => t.priority === 'medium').length, color: '#f59e0b' },
    { label: 'Baja',  value: tasks.filter(t => t.priority === 'low').length,    color: '#6b7280' },
  ].filter(d => d.value > 0)

  const consByStatus = [
    { name: 'Activas',     value: consultancies.filter(c => c.status === 'active').length,    color: CONS_COLORS[0] },
    { name: 'Pausadas',    value: consultancies.filter(c => c.status === 'paused').length,    color: CONS_COLORS[1] },
    { name: 'Completadas', value: consultancies.filter(c => c.status === 'completed').length, color: CONS_COLORS[2] },
  ].filter(d => d.value > 0)

  const progressData = consultancies
    .map(c => {
      const p = progressMap[c.id]
      return {
        name: c.name.length > 20 ? c.name.slice(0, 18) + '…' : c.name,
        pct:  p && p.total > 0 ? Math.round((p.done / p.total) * 100) : 0,
        done:  p?.done  ?? 0,
        total: p?.total ?? 0,
      }
    })
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 8)

  const today       = new Date().toISOString().split('T')[0]
  const totalTasks  = tasks.length
  const doneTasks   = tasks.filter(t => t.status === 'completed').length
  const overdueTasks = tasks.filter(t => t.due_date && t.due_date < today && t.status !== 'completed').length
  const globalPct   = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-sm">
        <p className="font-semibold text-gray-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color ?? p.fill }}>
            {p.name ?? p.dataKey}: <strong>{p.value}</strong>
          </p>
        ))}
      </div>
    )
  }

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 size={22} className="text-brand-600" />
          Analítica
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Métricas y progreso de todas las consultorías</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total tareas',    value: totalTasks,   icon: TrendingUp,   color: 'text-brand-600', bg: 'bg-brand-50'  },
          { label: 'Completadas',     value: doneTasks,    icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50'  },
          { label: 'Completado',      value: `${globalPct}%`, icon: BarChart3, color: 'text-blue-600',  bg: 'bg-blue-50'   },
          { label: 'Tareas vencidas', value: overdueTasks, icon: AlertTriangle,color: 'text-red-600',   bg: 'bg-red-50'    },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="card">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${bg}`}><Icon size={20} className={color} /></div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{value}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks by status — bar */}
        <div className="card lg:col-span-2">
          <h2 className="font-semibold text-gray-800 mb-4 text-sm">Tareas por estado</h2>
          {tasksByStatus.length === 0
            ? <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={tasksByStatus} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {tasksByStatus.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </div>

        {/* Consultancies by status — pie */}
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-4 text-sm">Consultorías por estado</h2>
          {consByStatus.length === 0
            ? <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={consByStatus}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={40}
                    paddingAngle={3}
                  >
                    {consByStatus.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )
          }
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Progress per consultancy */}
        <div className="card lg:col-span-2">
          <h2 className="font-semibold text-gray-800 mb-4 text-sm">Progreso por consultoría (% tareas completadas)</h2>
          {progressData.length === 0
            ? <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>
            : (
              <ResponsiveContainer width="100%" height={Math.max(180, progressData.length * 36)}>
                <BarChart
                  data={progressData}
                  layout="vertical"
                  margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null
                      const d = payload[0]?.payload
                      return (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-sm">
                          <p className="font-semibold text-gray-700 mb-1">{label}</p>
                          <p className="text-brand-600">{d?.done}/{d?.total} tareas · {d?.pct}%</p>
                        </div>
                      )
                    }}
                  />
                  <Bar dataKey="pct" radius={[0, 4, 4, 0]} fill="#1e3a5f">
                    {progressData.map((d, i) => (
                      <Cell key={i} fill={d.pct >= 80 ? '#22c55e' : d.pct >= 40 ? '#3b82f6' : '#1e3a5f'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )
          }
        </div>

        {/* Tasks by priority — pie */}
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-4 text-sm">Tareas por prioridad</h2>
          {tasksByPriority.length === 0
            ? <p className="text-sm text-gray-400 text-center py-8">Sin datos</p>
            : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={tasksByPriority}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={40}
                    paddingAngle={3}
                  >
                    {tasksByPriority.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            )
          }
        </div>
      </div>

      {/* Consultancy detail table */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-4 text-sm">Resumen de consultorías</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Consultoría</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Empresa</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Estado</th>
                <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Tareas</th>
                <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Progreso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {consultancies.map(c => {
                const p = progressMap[c.id]
                const pct = p && p.total > 0 ? Math.round((p.done / p.total) * 100) : 0
                const statusLabel = { active: 'Activa', paused: 'Pausada', completed: 'Completada' }
                const statusClass = { active: 'text-green-600 bg-green-50', paused: 'text-yellow-600 bg-yellow-50', completed: 'text-brand-600 bg-brand-50' }
                return (
                  <tr key={c.id} className="hover:bg-gray-50/50">
                    <td className="py-3 pr-4 font-medium text-gray-800">{c.name}</td>
                    <td className="py-3 pr-4 text-gray-500">{c.client_company}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClass[c.status]}`}>
                        {statusLabel[c.status]}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right text-gray-500">
                      {p ? `${p.done}/${p.total}` : '—'}
                    </td>
                    <td className="py-3 text-right">
                      <span className={`font-semibold ${pct >= 80 ? 'text-green-600' : pct >= 40 ? 'text-blue-600' : 'text-gray-500'}`}>
                        {p ? `${pct}%` : '—'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
